import { useTranslation } from '@hbs-us/ui';
import logoUrl from '@hbs-us/ui/assets/logo.svg';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Link,
  RouterProvider,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './app/AppShell.js';
import { rememberBlockedRoute } from './app/BlockedRouteNotice.js';
import { ErrorBoundary } from './app/ErrorBoundary.js';
import type { SessionFeatures, SessionUser } from './auth/authClient.js';
import { canOpenRoute, NO_ACCESS_ROUTE, resolveLandingRoute } from './auth/landing.js';
import { tenantHasRoute } from './auth/useFeatureAccess.js';
import { useSession } from './auth/useSession.js';
import { ActivityRoute } from './features/activity/ActivityRoute.js';
import { validateActivitySearch } from './features/activity/searchParams.js';
import { activityQueryKey, buildActivityPath } from './features/activity/useActivityQuery.js';
import { BillingPage } from './features/billing/BillingPage.js';
import { ClientDetailRoute } from './features/clients/ClientDetailRoute.js';
import { ClientForm } from './features/clients/ClientForm.js';
import { ClientsRoute } from './features/clients/ClientsRoute.js';
import { MergeRoute } from './features/clients/MergeRoute.js';
import { validateClientsSearch } from './features/clients/searchParams.js';
import { clientsQueryKey, clientsRequestPath } from './features/clients/useClientsQuery.js';
import { DispatchRoute } from './features/dispatch/DispatchRoute.js';
import { validateDispatchSearch } from './features/dispatch/searchParams.js';
import {
  dispatchRequestPath,
  dispatchWeekQueryKey,
} from './features/dispatch/useDispatchWeekQuery.js';
import { EmployeeDetailRoute } from './features/employees/EmployeeDetailRoute.js';
import { EmployeeForm } from './features/employees/EmployeeForm.js';
import { EmployeesRoute } from './features/employees/EmployeesRoute.js';
import { validateEmployeesSearch } from './features/employees/searchParams.js';
import { employeesQueryKey, employeesRequestPath } from './features/employees/useEmployeesQuery.js';
import { EstimateDetailRoute } from './features/estimates/EstimateDetailRoute.js';
import { EstimateEditRoute, EstimateForm } from './features/estimates/EstimateForm.js';
import { EstimatesRoute } from './features/estimates/EstimatesRoute.js';
import { validateEstimatesSearch } from './features/estimates/searchParams.js';
import { estimatesQueryKey, estimatesRequestPath } from './features/estimates/useEstimatesQuery.js';
import { FleetRoute } from './features/fleet/FleetRoute.js';
import { ScheduleRoute } from './features/fleet/ScheduleRoute.js';
import { validateFleetSearch } from './features/fleet/searchParams.js';
import { fleetQueryKey, fleetRequestPath } from './features/fleet/useFleetQuery.js';
import { VehicleDetailRoute } from './features/fleet/VehicleDetailRoute.js';
import { VehicleFormRoute } from './features/fleet/VehicleFormRoute.js';
import { HomeRoute } from './features/home/HomeRoute.js';
import { CategoryManageView } from './features/inventory/CategoryManageView.js';
import { InventoryItemFormRoute } from './features/inventory/InventoryItemFormRoute.js';
import { InventoryRoute } from './features/inventory/InventoryRoute.js';
import { ItemDetailRoute } from './features/inventory/ItemDetailRoute.js';
import { validateInventorySearch } from './features/inventory/searchParams.js';
import { inventoryQueryKey, inventoryRequestPath } from './features/inventory/useInventoryQuery.js';
import { InvoiceDetailRoute } from './features/invoices/InvoiceDetailRoute.js';
import { InvoiceEditRoute, InvoiceForm } from './features/invoices/InvoiceForm.js';
import { InvoicesRoute } from './features/invoices/InvoicesRoute.js';
import { validateInvoicesSearch } from './features/invoices/searchParams.js';
import { invoicesQueryKey, invoicesRequestPath } from './features/invoices/useInvoicesQuery.js';
import { JobDetailRoute } from './features/jobs/JobDetailRoute.js';
import { JobEditRoute } from './features/jobs/JobEditRoute.js';
import { JobForm } from './features/jobs/JobForm.js';
import { JobsRoute } from './features/jobs/JobsRoute.js';
import { validateJobsSearch } from './features/jobs/searchParams.js';
import { jobsQueryKey, jobsRequestPath } from './features/jobs/useJobsQuery.js';
import { MonthlyBillsRoute } from './features/monthlyBills/MonthlyBillsRoute.js';
import { validateMonthlyBillsSearch } from './features/monthlyBills/searchParams.js';
import {
  monthlyBillsQueryKey,
  monthlyBillsRequestPath,
} from './features/monthlyBills/useMonthlyBillsQuery.js';
import { PayrollCenterRoute } from './features/payroll/PayrollCenterRoute.js';
import { PayrollHistoryRoute } from './features/payroll/PayrollHistoryRoute.js';
import { PayrollRunDetailRoute } from './features/payroll/PayrollRunDetailRoute.js';
import { validatePayrollSearch } from './features/payroll/searchParams.js';
import { payrollQueryKey, payrollRequestPath } from './features/payroll/usePayrollQuery.js';
import { QcJoinRoute } from './features/qc/QcJoinRoute.js';
import { QcChecklistRoute, QcListRoute } from './features/qc/QcRoutes.js';
import { ReportsRoute } from './features/reports/ReportsRoute.js';
import { validateReportsSearch } from './features/reports/searchParams.js';
import { reportsQueryKey, reportsRequestPath } from './features/reports/useReportsQuery.js';
import { AgreementDetailRoute } from './features/serviceAgreements/AgreementDetailRoute.js';
import { AgreementForm } from './features/serviceAgreements/AgreementForm.js';
import { ServiceAgreementsRoute } from './features/serviceAgreements/ServiceAgreementsRoute.js';
import { validateServiceAgreementsSearch } from './features/serviceAgreements/searchParams.js';
import {
  agreementsQueryKey,
  agreementsRequestPath,
} from './features/serviceAgreements/useAgreementsQuery.js';
import { ServiceTitanRoute } from './features/servicetitan/ServiceTitanRoute.js';
import { validateServiceTitanSearch } from './features/servicetitan/searchParams.js';
import { NotificationSettingsScreen } from './features/settings/NotificationSettingsScreen.js';
import { SettingsRoute } from './features/settings/SettingsRoute.js';
import { SupportRoute } from './features/support/SupportRoute.js';
import { validateTimesheetsSearch } from './features/timesheets/searchParams.js';
import { TimesheetsRoute } from './features/timesheets/TimesheetsRoute.js';
import {
  timesheetsQueryKey,
  timesheetsRequestPath,
} from './features/timesheets/useTimesheetsQuery.js';
import { AccessView } from './features/users/AccessView.js';
import { MyAccountRoute } from './features/users/MyAccountRoute.js';
import { validateUsersSearch } from './features/users/searchParams.js';
import { UserForm } from './features/users/UserFormRoute.js';
import { UsersRoute } from './features/users/UsersRoute.js';
import { usersQueryKey } from './features/users/useUsersQuery.js';
import { apiFetchJson } from './lib/apiFetch.js';
import { queryClient } from './lib/queryClient.js';

/**
 * TanStack Router instance (client-only SPA — Discretion #1).
 *
 * The root route renders the durable AppShell (sidebar / top bar / language
 * switcher). Every subsystem is now a real route (the last ComingSoon stub,
 * `/timesheets`, is promoted in Phase 22 Plan 13). The index redirects to the
 * reports room.
 */

/**
 * Branded not-found room. Any unknown/stale path renders this instead of
 * TanStack's bare "Not Found" text (22-UAT test 5b) — a real heading plus a
 * focusable, typed `<Link>` home (A11Y-01). Token-styled only (no hex — BRAND-06).
 */
/**
 * A signed-in user who can reach nothing at all.
 *
 * No retry and no navigation: there is nowhere to send them, and offering a button that goes
 * somewhere they cannot open would be worse than saying so plainly. The fix is somebody granting
 * them a permission, so the copy points at that.
 */
function NoAccessPage() {
  const { t } = useTranslation();
  return (
    <section className="flex h-full flex-col items-center justify-center gap-4 bg-background px-6 py-16 text-center">
      <img src={logoUrl} alt="" aria-hidden="true" className="h-8 w-auto opacity-80" />
      <h1 className="font-display font-semibold text-2xl text-foreground">
        {t('common:noAccess.heading')}
      </h1>
      <p className="max-w-sm text-muted-foreground text-sm">{t('common:noAccess.body')}</p>
    </section>
  );
}

function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <section className="flex h-full flex-col items-center justify-center gap-4 bg-background px-6 py-16 text-center">
      <img src={logoUrl} alt="" aria-hidden="true" className="h-8 w-auto opacity-80" />
      <h1 className="font-display text-2xl font-semibold text-foreground">
        {t('common:notFound.heading')}
      </h1>
      <p className="max-w-sm text-sm text-muted-foreground">{t('common:notFound.body')}</p>
      <Link
        to="/reports"
        search={{ range: '1m' }}
        className="text-sm font-semibold text-primary-ink underline underline-offset-4"
      >
        {t('common:notFound.link')}
      </Link>
    </section>
  );
}

/**
 * Start a screen's first request before the screen exists.
 *
 * Every list route rendered a full-pane `Spinner` on `bg-background` while its first query
 * resolved — 44 files did, behind one of 46 `isPending` early returns — so switching apps
 * blanked the pane and, in the dark theme, went dark. Nothing was remounting: the shell and
 * the session sit above the router and never unmount. The pane was simply empty because
 * nothing had asked the api for anything until the component had already mounted and
 * rendered its empty state.
 *
 * `defaultPreload: 'intent'` makes the router run a route's loader when a link to it is
 * hovered or focused, so the request is usually finished by the time the click lands and
 * the component reads the answer straight out of the cache with no pending state at all.
 *
 * ⚠ IT PREFETCHES, IT NEVER AWAITS AND IT NEVER THROWS.
 *
 *  - No await, so a navigation is never held behind a slow network. The component mounts
 *    at once and TanStack Query dedupes: the in-flight request the hover started is the
 *    same one the hook subscribes to, never a second call.
 *  - `prefetchQuery`, not `ensureQueryData`. `ensureQueryData` REJECTS on a failed fetch,
 *    which would surface as the router's error component and take away each screen's own
 *    announced error state and its retry button. `prefetchQuery` swallows the failure and
 *    lets the component ask again and render its own answer.
 *
 * The key and the path come from the feature's own exported helpers, and `loaderDeps` hands
 * over the SAME validated search the component reads, so the warmed entry and the entry the
 * hook looks up cannot drift into two different cache keys.
 */
function warmRoute(queryKey: readonly unknown[], path: string): void {
  void queryClient.prefetchQuery({ queryKey, queryFn: () => apiFetchJson<unknown>(path) });
}

/** What `beforeLoad` needs to answer an access question, supplied by AppRouter. */
export interface RouterContext {
  user: SessionUser | null;
  /** What the TENANT has, as opposed to what the user may do. See auth/useFeatureAccess.ts. */
  features: SessionFeatures | null;
}

/**
 * True when `beforeLoad` is running for a link PRELOAD rather than a real navigation.
 *
 * The `preload` flag `beforeLoad` receives is not enough on its own. The router reports it as
 * false for any match that is already mounted, and the ROOT route is always mounted, so on the
 * root's guard it is false even while a hover preloads some other page. A real navigation loads
 * the router's own `latestLocation`; a preload builds a fresh location object, so the two only
 * differ for a preload.
 */
function isPreloadOnly(location: object): boolean {
  return (router as { latestLocation?: object }).latestLocation !== location;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  /**
   * The access gate for EVERY route, in one place.
   *
   * Annotating 48 route definitions would have worked and would have rotted: the next route added
   * is the one somebody forgets. The root's `beforeLoad` runs on every navigation, so an unlisted
   * route is caught here whether or not anyone remembered it.
   *
   * A blocked user is SENT to where they belong rather than shown a wall. A crew member who opens a
   * shared `/jobs` link wants to be somewhere they can work, not to be told off. The routes people
   * are sent TO are ungated (see landing.ts), so this cannot loop.
   *
   * Still not authorization. The api worker refuses these requests independently; this only stops
   * someone rendering a screen that is about to 403 at them.
   */
  beforeLoad: ({ context, location, preload }) => {
    // A destination the TENANT does not have is treated exactly like one the USER cannot reach:
    // sent home rather than shown a wall. Typing /servicetitan by hand on a tenant without the
    // integration lands on the same screen a hidden nav item would have kept them on, and the api
    // worker 404s the data either way.
    const canOpen = canOpenRoute(context.user, location.pathname);
    const tenantHasFeature = tenantHasRoute(context.features, location.pathname);
    if (canOpen && tenantHasFeature) {
      return;
    }
    const landing = resolveLandingRoute(context.user);
    // A hover or touch only PRELOADS the link, and `beforeLoad` runs for that too. Nobody has
    // been sent anywhere yet, so explaining a redirect that did not happen would be a false notice.
    if (!(preload || isPreloadOnly(location)) && landing.to !== NO_ACCESS_ROUTE) {
      rememberBlockedRoute(tenantHasFeature ? 'permission' : 'feature', landing.to);
    }
    throw redirect({ ...landing, replace: true } as never);
  },
  component: AppShell,
  errorComponent: () => (
    <ErrorBoundary>
      <span />
    </ErrorBoundary>
  ),
  notFoundComponent: NotFoundPage,
});

// `/login` is an entry alias, not a screen: after signing in from `/login` with
// no intended destination the router would otherwise match nothing and render
// not-found. Redirect it to the canonical default (same target as `/`).
const loginRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: ({ context }) => {
    // This used to send EVERYONE to `/reports`, which needs `reports.view` — a permission crew
    // members do not hold. Signing in from here put them on a locked screen.
    throw redirect({ ...resolveLandingRoute(context.user), replace: true } as never);
  },
});

/**
 * The index renders the Home KPI dashboard, but only for someone who can read it.
 *
 * The bounce used to live in a `useEffect` inside HomeRoute, which meant it ran after a render and
 * after firing `GET /api/v1/overview` — a request behind `reports.view` that an Employee is
 * guaranteed to be refused. Deciding here costs neither the flash nor the failed request.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    const landing = resolveLandingRoute(context.user);
    if (landing.to === '/') return;
    throw redirect({ ...landing, replace: true } as never);
  },
  component: HomeRoute,
});

/**
 * The end of the ladder in landing.ts: a signed-in user who can open nothing at all.
 *
 * Rare, and it must exist anyway — a resolver that cannot resolve has to land somewhere, and a
 * redirect loop is a far worse answer than a page saying so.
 */
/**
 * Quality control. The `/qc` prefix is gated on the tenant's tier by the api (Pro+) and on the
 * qc.view key by the root access gate; the route itself carries no third check.
 */
const qcListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qc',
  component: QcListRoute,
});

const qcChecklistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qc/$id',
  component: QcChecklistRoute,
});

/**
 * Where a scanned QR lands. Static segment, so it is matched before `/qc/$id` treats "join" as an
 * id — the order below is load-bearing.
 */
const qcJoinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qc/join/$token',
  component: QcJoinRoute,
});

const serviceTitanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/servicetitan',
  component: ServiceTitanRoute,
  // `?week=` in the URL, exactly like /dispatch — deep-linkable and back-button friendly.
  validateSearch: validateServiceTitanSearch,
});

const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NO_ACCESS_ROUTE,
  component: NoAccessPage,
});

// The reports room: the real route with typed, deep-linkable search params
// (range/start/end). A mistyped `range` fails the TS build (Discretion #1).
const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  validateSearch: validateReportsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(reportsQueryKey(deps), reportsRequestPath(deps)),
  component: ReportsRoute,
});

// Settings → Notifications: the real route (master push + per-category toggles + the self-serve
// delete entry). The bell lives in the shell TopBar; this is its companion preferences room.
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  component: NotificationSettingsScreen,
});

// The invoices room: a real route (promoted from a ComingSoon stub — Phase 21).
// Typed, deep-linkable `?show_archived` search params drive the coarse-endpoint
// refetch.
const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices',
  validateSearch: validateInvoicesSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(invoicesQueryKey(deps), invoicesRequestPath(deps)),
  component: InvoicesRoute,
});

// Create route (static segment wins over the `$id` dynamic segment below).
const invoiceNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/new',
  component: () => <InvoiceForm mode="create" />,
});

// Invoice detail route (faithful 1:1 detail port).
const invoiceDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$id',
  component: InvoiceDetailRoute,
});

// Edit route (21-11): the full invoice editor wired to PUT /api/v1/invoices/:id,
// prefilled from the enriched detail query.
const invoiceEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invoices/$id/edit',
  component: InvoiceEditRoute,
});

// The Clients room: a NEW room. Typed, deep-linkable `?q` search param drives the
// server-side list search.
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients',
  validateSearch: validateClientsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(clientsQueryKey(deps), clientsRequestPath(deps)),
  component: ClientsRoute,
});

// Create route (static segment wins over the `$id` dynamic segment below).
const clientNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/new',
  component: () => <ClientForm mode="create" />,
});

// Merge screen (static segment — MUST precede the `/clients/$id` dynamic route
// below, or "merge" is read as an id). The duplicate groups ride the router state.
const clientsMergeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/merge',
  component: MergeRoute,
});

// Client detail route (header, portal switch, notes, estimates/invoices/jobs).
const clientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$id',
  component: ClientDetailRoute,
});

// Edit route — the same form component as create, prefilled from the detail query.
const clientEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/clients/$id/edit',
  component: () => <ClientForm mode="edit" />,
});

// The estimates room: a real route (promoted from a ComingSoon stub — Phase 21).
// Typed, deep-linkable `?status` + `?show_archived` search params drive the
// coarse-endpoint refetch + the status-filter pills (each pill NAVIGATES).
const estimatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/estimates',
  validateSearch: validateEstimatesSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(estimatesQueryKey(deps), estimatesRequestPath(deps)),
  component: EstimatesRoute,
});

// Create route (static segment wins over the `$id` dynamic segment below).
const estimateNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/estimates/new',
  component: () => <EstimateForm mode="create" />,
});

// Estimate detail route (faithful 1:1 detail port; the customer-approval
// signature canvas STAYS legacy SSR — the detail view only links out, D-02).
const estimateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/estimates/$id',
  component: EstimateDetailRoute,
});

// Edit route (the api-host edit endpoint is not shipped yet — the form renders
// an informative notice; the route is wired for when it lands).
const estimateEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/estimates/$id/edit',
  component: EstimateEditRoute,
});

// The employees room: a real route (promoted from a ComingSoon stub — Phase 22).
// Typed, deep-linkable `?q` + `?show_archived` search params drive the in-memory text
// filter + the coarse-endpoint active/archived refetch.
const employeesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees',
  validateSearch: validateEmployeesSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(employeesQueryKey(deps), employeesRequestPath(deps)),
  component: EmployeesRoute,
});

// Create route (static segment wins over the `$id` dynamic segment below).
const employeeNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/new',
  component: () => <EmployeeForm mode="create" />,
});

// Employee detail route (the redesigned profile detail).
const employeeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/$id',
  component: EmployeeDetailRoute,
});

// Edit route (the api-host edit endpoint is not shipped yet — the form renders
// an informative notice; the route is wired for when it lands).
const employeeEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/employees/$id/edit',
  component: () => <EmployeeForm mode="edit" />,
});

// The People & Access (users) cluster: NEW routes (never stubbed — Phase 22 Plan 06).
// The list carries typed, deep-linkable `?q` + `?role` search params; the static
// segments (`/users/new`, `/users/permissions`, `/account`) are declared BEFORE the
// dynamic `/users/$id` so the router prefers the literal match.
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  validateSearch: validateUsersSearch,
  loader: () => warmRoute(usersQueryKey(), '/api/v1/users'),
  component: UsersRoute,
});

// Invite (create) route — fully interactive (POST /api/v1/users is shipped, 22-06).
const userNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/new',
  component: () => <UserForm mode="create" />,
});

// People & Access (the SEC-sensitive privilege-grant surface): pick a person, pick a role,
// with the 35-key override screen behind Advanced. Static segment — MUST precede the
// `/users/$id` dynamic route below. The path is unchanged so existing links still land.
const userPermissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/permissions',
  component: AccessView,
});

// User detail/edit route (the full user-record edit stays SSR; the role + per-permission
// overrides are editable on the matrix — the edit view links there, D-03).
const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users/$id',
  component: () => <UserForm mode="edit" />,
});

// My Account — the signed-in user's own profile + password (PATCH /api/v1/account is
// shipped, 22-06; self-only, server resolves the principal).
const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: MyAccountRoute,
});

const accountSecurityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account/security',
  beforeLoad: () => {
    throw redirect({ to: '/account', replace: true } as never);
  },
});

// The final People & Access cluster (Phase 22 Plan 07): NEW routes (never stubbed).
// Settings is the company/profile form (GET/PATCH /api/v1/settings); the existing
// `/settings/notifications` notification-preferences route is separate and stays.
const companySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsRoute,
});

// Support — submit a request + view this tenant's ticket history (GET /api/v1/support/tickets
// + POST /api/v1/support, 22-07).
const supportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/support',
  component: SupportRoute,
});

// Activity — the READ-ONLY audit feed with typed, deep-linkable `?type` + `?from` + `?to` +
// `?q` filters (GET /api/v1/activity, 22-07). The GET never mutates (Pitfall 3).
const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity',
  validateSearch: validateActivitySearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(activityQueryKey(deps), buildActivityPath(deps)),
  component: ActivityRoute,
});

// The inventory room: a real route (promoted from a ComingSoon stub — Phase 22 Plan 08).
// Typed, deep-linkable `?category` + `?archived` search params drive the category-filter pills
// + the coarse-endpoint active/archived refetch. The custom-per-category field model is preserved
// — the item detail renders each category's custom field definitions dynamically.
const inventoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory',
  validateSearch: validateInventorySearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(inventoryQueryKey(deps), inventoryRequestPath(deps)),
  component: InventoryRoute,
});

// Category management (static segment — MUST precede the `/inventory/$id` dynamic route below).
const inventoryCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/categories',
  component: CategoryManageView,
});

// Item create route (static segments MUST precede the `/inventory/$id` dynamic route below).
const inventoryItemNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/items/new',
  component: () => <InventoryItemFormRoute mode="create" />,
});

// Item edit route (22-25) — a sibling of the new-item route. The shared form runs in edit mode:
// it resolves the item from the coarse query by the `$id` param, prefills it, locks the category,
// and submits the full-edit PATCH. The more-specific `/edit` segment matches before `/inventory/$id`.
const inventoryItemEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/$id/edit',
  component: () => <InventoryItemFormRoute mode="edit" />,
});

// Item detail route (renders the dynamic per-category custom fields).
const inventoryItemDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventory/$id',
  component: ItemDetailRoute,
});

// The fleet room: a real route (promoted from a ComingSoon stub — Phase 22 Plan 09).
// Typed, deep-linkable `?entryType` + `?maintenanceCategory` + `?archived` search params drive
// the coarse-endpoint refetch + the filter pills (each pill NAVIGATES). Vehicles render as a CARD
// GRID (not a table) — fleet vehicles are inherently visual/sparse (UI-SPEC archetype A card variant).
const fleetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet',
  validateSearch: validateFleetSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(fleetQueryKey(deps), fleetRequestPath(deps)),
  component: FleetRoute,
});

// Tenant-wide schedule route (static segment — MUST precede the `/fleet/$id` dynamic route).
const fleetScheduleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/schedule',
  component: ScheduleRoute,
});

// Vehicle create route (static segments MUST precede the `/fleet/$id` dynamic route below).
const fleetVehicleNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/vehicles/new',
  component: () => <VehicleFormRoute mode="create" />,
});

// Vehicle edit route (static `/edit` suffix — a sibling of the create route, prefills from the
// detail read and PATCHes the update — 22-26 F7).
const fleetVehicleEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/$id/edit',
  component: () => <VehicleFormRoute mode="edit" />,
});

// Vehicle detail route (tabbed: Overview · Maintenance Schedule · Documents/Packet). The document
// packet downloads via the REUSED api-host GET (D-06); the engine is never rebuilt client-side.
const fleetVehicleDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fleet/$id',
  component: VehicleDetailRoute,
});

// The jobs room: a real route (promoted from a ComingSoon stub — Phase 22 Plan 10). Typed,
// deep-linkable `?status` + `?show_archived` search params drive the coarse-endpoint refetch + the
// status-filter pills (each pill NAVIGATES; the 4 ALLOWED_JOB_STATUSES with server counts).
const jobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs',
  validateSearch: validateJobsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(jobsQueryKey(deps), jobsRequestPath(deps)),
  component: JobsRoute,
});

// Job create route — a real form (POST /api/v1/jobs). The static `/jobs/new` path takes
// precedence over the `/jobs/$id` param route (Plan 22-22).
const jobNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/new',
  component: () => <JobForm mode="create" />,
});

// Job edit route.
const jobEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$id/edit',
  component: JobEditRoute,
});

// Job detail route (tabbed: Overview · Line Items · Expenses/Income · Profitability · Blueprints).
// The former standalone job-financials screen is FOLDED into the Profitability TAB (D-05) — it is
// a tab inside the detail, NOT a router route. The `$id` segment also hosts `new` (the create
// form) in the route body branch.
const jobDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/jobs/$id',
  component: JobDetailRoute,
});

// The dispatch room: a NEW route (never a ComingSoon stub — Phase 22 Plan 11, the flagship). A
// typed, deep-linkable `?week=YYYY-MM-DD` search param selects the week; the server resolves the
// Monday-start week + aggregates the assignments. The board is a fully-accessible @dnd-kit
// scheduling surface (KeyboardSensor + aria-live announcements + a non-drag "Schedule…" fallback
// on every card — A11Y-01). The public status-token page stays legacy SSR (D-06).
const dispatchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dispatch',
  validateSearch: validateDispatchSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(dispatchWeekQueryKey(deps), dispatchRequestPath(deps)),
  component: DispatchRoute,
});

// The Recurring cluster (Phase 22 Plan 12). Service Agreements is a real route (promoted from a
// ComingSoon stub); the typed `?status` search param drives the All/Active/Paused/Cancelled
// status-filter pills (each pill NAVIGATES; server counts). The list GET is READ-ONLY —
// auto-invoicing runs off-page in Cron+Queue; only the explicit detail "Generate invoice" action
// creates one invoice (D-03).
const serviceAgreementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service-agreements',
  validateSearch: validateServiceAgreementsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(agreementsQueryKey(deps), agreementsRequestPath(deps)),
  component: ServiceAgreementsRoute,
});

// Create route (static segment wins over the `$id` dynamic segment below). The carve ships the
// agreement create POST this plan, so this form is fully interactive.
const serviceAgreementNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service-agreements/new',
  component: () => <AgreementForm mode="create" />,
});

// Edit route (static `/edit` suffix wins over the bare `$id` detail; the carve ships the agreement
// edit PATCH this plan, so this form is fully interactive and prefills from the coarse list row).
const serviceAgreementEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service-agreements/$id/edit',
  component: () => <AgreementForm mode="edit" />,
});

// Agreement detail route (schedule + linked job + next-run; the single generate-invoice action +
// pause/resume/cancel/archive + the generated-invoices table).
const serviceAgreementDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/service-agreements/$id',
  component: AgreementDetailRoute,
});

// Monthly Bills — a NEW route (never a ComingSoon stub). The typed `?month=YYYY-MM` search param
// selects which month the totals strip + the per-bill paid badges reflect. The list GET is read-only;
// add/edit + mark-paid ride the financials.edit gate server-side.
const monthlyBillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/monthly-bills',
  validateSearch: validateMonthlyBillsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(monthlyBillsQueryKey(deps), monthlyBillsRequestPath(deps)),
  component: MonthlyBillsRoute,
});

// Timesheets — the heaviest screen, promoted from a ComingSoon stub this plan. The typed
// `?week=YYYY-MM-DD` search param selects which week the grid + totals reflect (week-nav prev/next
// links carry it). The list GET is read-only; the lunch/clock math stays SERVER-SIDE (D-03).
const timesheetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/timesheets',
  validateSearch: validateTimesheetsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(timesheetsQueryKey(deps), timesheetsRequestPath(deps)),
  component: TimesheetsRoute,
});

// Payroll — the most sensitive, money-committing screen (Phase 22 Plan 14). The typed
// `?week=YYYY-MM-DD` search param selects the pay period the computed-gross review grid reflects. The
// list GET is read-only; the gross-pay math stays SERVER-SIDE (golden calc, D-03). Run Payroll commits
// a locked, immutable run (no optimistic UI). History + Run Detail are deep-linkable real routes.
const payrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll',
  validateSearch: validatePayrollSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => warmRoute(payrollQueryKey(deps), payrollRequestPath(deps)),
  component: PayrollCenterRoute,
});

const payrollHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll/history',
  component: PayrollHistoryRoute,
});

const payrollRunDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/payroll/runs/$id',
  component: PayrollRunDetailRoute,
});

// Billing — a real SPA room as of 27.2-05 (it was the last legacy exception, D-01/CARVE-07). It
// renders GET /api/v1/billing directly: subscription state, the self-serve tiers straight from the
// pricing config, Stripe checkout / portal / resync for Admins, and the subscription invoices.
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: BillingPage,
});

// Every cut-over subsystem room is now a real route, billing included — no room hops back to
// legacy SSR. `<Link to="/timesheets">` and the rest compile-check against the typed route tree
// (Discretion #1).

const routeTree = rootRoute.addChildren([
  indexRoute,
  serviceTitanRoute,
  noAccessRoute,
  qcListRoute,
  qcJoinRoute,
  qcChecklistRoute,
  loginRedirectRoute,
  reportsRoute,
  settingsRoute,
  invoicesRoute,
  invoiceNewRoute,
  invoiceDetailRoute,
  invoiceEditRoute,
  clientsRoute,
  clientNewRoute,
  clientsMergeRoute,
  clientDetailRoute,
  clientEditRoute,
  estimatesRoute,
  estimateNewRoute,
  estimateDetailRoute,
  estimateEditRoute,
  employeesRoute,
  employeeNewRoute,
  employeeDetailRoute,
  employeeEditRoute,
  usersRoute,
  userNewRoute,
  userPermissionsRoute,
  userDetailRoute,
  accountRoute,
  accountSecurityRoute,
  companySettingsRoute,
  supportRoute,
  activityRoute,
  inventoryRoute,
  inventoryCategoriesRoute,
  inventoryItemNewRoute,
  inventoryItemEditRoute,
  inventoryItemDetailRoute,
  fleetRoute,
  fleetScheduleRoute,
  fleetVehicleNewRoute,
  fleetVehicleEditRoute,
  fleetVehicleDetailRoute,
  jobsRoute,
  jobNewRoute,
  jobEditRoute,
  jobDetailRoute,
  dispatchRoute,
  serviceAgreementsRoute,
  serviceAgreementNewRoute,
  serviceAgreementEditRoute,
  serviceAgreementDetailRoute,
  monthlyBillsRoute,
  timesheetsRoute,
  payrollRoute,
  payrollHistoryRoute,
  payrollRunDetailRoute,
  billingRoute,
]);

export const router = createRouter({
  routeTree,
  // Run a route's loader when a link to it is hovered or focused, so the request that used
  // to start on mount starts on intent instead.
  defaultPreload: 'intent',
  // The router caches a loader's own result for 30s by default, which would stop it calling
  // the loader again. There is nothing to cache here — the loader returns void and TanStack
  // Query owns the cache — so let the loader run every time and let react-query's own
  // `staleTime` (30s, lib/queryClient.ts) decide whether a request is actually made.
  defaultPreloadStaleTime: 0,
  // Replaced on every render by AppRouter below; `null` is only the pre-mount value.
  context: { user: null, features: null } as RouterContext,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter() {
  // AppRouter renders inside AuthGuard, so a session always exists by the time routes mount.
  // Feeding it through the router context is what lets `beforeLoad` answer an access question
  // BEFORE a screen renders, which a hook inside a component cannot do.
  const { session } = useSession();
  return (
    <RouterProvider
      router={router}
      context={{ user: session?.user ?? null, features: session?.features ?? null }}
    />
  );
}
